import { Box, Typography, Grid } from "@mui/joy";
import { AnimatePresence, motion } from "framer-motion";
import { useMaterials } from "../hooks/useMaterials";
import { ProductCard } from "../ProductCard";
import { MultiLineProductCard } from "../MultiLineProductCard";
import { SupplierComparisonRefresh as SupplierComparison } from "../../..";
import { Package } from "lucide-react";
import { MaterialsSkeleton } from "../skeletons";
import {
    HIDDEN_MATERIAL_CATEGORIES,
    isMultiLineCategory,
    isMultiLineSelection,
    isVisibleMaterialCategory,
    type MaterialLine,
} from "../hooks/utils/materialLineHelpers";

interface MaterialsProps {
    measurements: any;
    attributes: any[];
    setIsNextStageEligible: (val: any) => void;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function getProductOptions(subcategories: any[]): string[] {
    return Array.from(new Set<string>(subcategories.map((s) => s.Name)));
}

function getColorOptions(subcategories: any[], selectedProduct: string | null): any[] {
    return subcategories.filter((s) => s.Name === selectedProduct && s.Color);
}

function getPriceDisplay(selection: any, priceRanges: any, categoryName: string): { display: string; label: string } {
    const selectedProduct = selection.value;
    const unitPriceNum = Number(selection.unitPrice);
    if (!isNaN(unitPriceNum) && unitPriceNum > 0) {
        return { display: `$${unitPriceNum.toFixed(2)}`, label: "Unit Price" };
    }
    if (selectedProduct && priceRanges[selectedProduct]) {
        const r = priceRanges[selectedProduct];
        return { display: `$${r.min.toFixed(2)} - $${r.max.toFixed(2)}`, label: "Unit Price Range" };
    }
    if (priceRanges[categoryName]) {
        const r = priceRanges[categoryName];
        return { display: `$${r.min.toFixed(2)} - $${r.max.toFixed(2)}`, label: "Price Range" };
    }
    return { display: "-", label: "Price" };
}

function getLinePriceDisplay(line: MaterialLine, priceRanges: any, categoryName: string): { display: string; label: string } {
    const unitPriceNum = Number(line.unitPrice);
    if (!isNaN(unitPriceNum) && unitPriceNum > 0) {
        return { display: `$${unitPriceNum.toFixed(2)}`, label: "Unit Price" };
    }
    if (line.value && priceRanges[line.value]) {
        const r = priceRanges[line.value];
        return { display: `$${r.min.toFixed(2)} - $${r.max.toFixed(2)}`, label: "Unit Price Range" };
    }
    if (priceRanges[categoryName]) {
        const r = priceRanges[categoryName];
        return { display: `$${r.min.toFixed(2)} - $${r.max.toFixed(2)}`, label: "Price Range" };
    }
    return { display: "-", label: "Price" };
}

function isPrerequisiteMet(selection: any): boolean {
    if (isMultiLineSelection(selection)) {
        return selection.lines.some(
            (line) => !!(line.value && line.quantity > 0 && line.manufacturerProductId && !line.awaitingColor),
        );
    }
    return !!(selection?.value && (selection?.quantity || 0) > 0 && !selection?.awaitingColor);
}

// ─── Single Row ──────────────────────────────────────────────────────────────

interface MaterialRowProps {
    item: any;
    selection: any;
    selectedValues: any;
    priceRanges: any;
    onProductChange: (val: any) => void;
    onColorChange: (color: string | null, mId?: string) => void;
    onQuantityUpdate: (dir: "add" | "remove") => void;
    onRemove: () => void;
    onReevaluate?: () => void;
}

function MaterialRow({
    item,
    selection,
    selectedValues,
    priceRanges,
    onProductChange,
    onColorChange,
    onQuantityUpdate,
    onRemove,
    onReevaluate,
}: MaterialRowProps) {
    const subcategories = item.Subcategories || [];

    const productOptions = getProductOptions(subcategories);

    // Only use the stored value if it actually exists in the options list.
    // If there's a mismatch (DB name ≠ option name), pass null to avoid the MUI warning.
    const selectedProduct = productOptions.includes(selection.value) ? selection.value : null;

    const matchingSubs = getColorOptions(subcategories, selectedProduct);
    const colorOptions = Array.from(new Set<string>(matchingSubs.map((s) => s.Color as string)));
    const { display: priceDisplay, label: priceLabel } = getPriceDisplay(selection, priceRanges, item.Name);

    const hasNoVal = !selection.value;
    const hasNoQty = !selection.quantity || selection.quantity === 0;

    return (
        <ProductCard
            categoryName={item.Name}
            units={Array.from(new Set(item.MeasurementType?.map((m: any) => m.Unit))).join(", ")}
            productOptions={productOptions}
            selectedProduct={selectedProduct}
            onProductChange={onProductChange}
            colorOptions={colorOptions}
            selectedColor={selection.color || null}
            onColorChange={(color) => {
                const matched = matchingSubs.find((s) => s.Color === color);
                onColorChange(color, matched?.Id);
            }}
            quantity={selection.quantity || 0}
            onQuantityUpdate={onQuantityUpdate}
            priceDisplay={priceDisplay}
            priceLabel={priceLabel}
            hasIssue={hasNoVal || hasNoQty}
            issueTitle={hasNoVal ? "Selection Required" : "Quantity is Zero"}
            prerequisites={item.PrerequisitesOn?.map((prereq: string) => ({
                name: prereq,
                isSatisfied: isPrerequisiteMet(selectedValues[prereq]),
            }))}
            onRemove={onRemove}
            onReevaluate={onReevaluate}
        />
    );
}

interface MultiLineMaterialRowProps {
    item: any;
    selection: any;
    selectedValues: any;
    priceRanges: any;
    onLineProductChange: (lineKey: string, product: string) => void;
    onLineQuantityUpdate: (lineKey: string, dir: "add" | "remove") => void;
    onAddLine: () => void;
    onRemoveLine: (lineKey: string) => void;
    onClearAll: () => void;
    onReevaluate?: () => void;
}

function MultiLineMaterialRow({
    item,
    selection,
    selectedValues,
    priceRanges,
    onLineProductChange,
    onLineQuantityUpdate,
    onAddLine,
    onRemoveLine,
    onClearAll,
    onReevaluate,
}: MultiLineMaterialRowProps) {
    const subcategories = item.Subcategories || [];
    const productOptions = getProductOptions(subcategories);
    const lines = isMultiLineSelection(selection) ? selection.lines : [];

    return (
        <MultiLineProductCard
            categoryName={item.Name}
            units={Array.from(new Set(item.MeasurementType?.map((m: any) => m.Unit))).join(", ")}
            productOptions={productOptions}
            lines={lines}
            getPriceDisplay={(line) => getLinePriceDisplay(line, priceRanges, item.Name)}
            prerequisites={item.PrerequisitesOn?.map((prereq: string) => ({
                name: prereq,
                isSatisfied: isPrerequisiteMet(selectedValues[prereq]),
            }))}
            onLineProductChange={onLineProductChange}
            onLineQuantityUpdate={onLineQuantityUpdate}
            onAddLine={onAddLine}
            onRemoveLine={onRemoveLine}
            onClearAll={onClearAll}
            onReevaluate={onReevaluate}
        />
    );
}

// ─── Material Groups ─────────────────────────────────────────────────────────

// Group order and labels aligned with Initial Inspection sections (InspectionV2 / PDF).
const MATERIAL_GROUPS: { label: string; items: string[] }[] = [
    {
        label: "Shingle & Roofing",
        items: [
            "Field Shingles",
            "Hip & Ridge",
            "Starter",
            "Double Starter",
            "Mod Bit Cap",
            "Mod Bit Base",
            "Mod Primer",
        ],
    },
    {
        label: "Underlayment & Shield",
        items: ["Field Dry In Synthetic", "Field Dry In 15 Felt", "Ice & Water Shield", "Valley Liner"],
    },
    {
        label: "Ventilation",
        items: ["Ridge Vent", "Box Roof Vent", "Turbine Roof Vent", "Bath Vent", "Dryer Vent"],
    },
    {
        label: "Penetrations",
        items: ["Furnace Jacks", "Furnace Cap", "Furnace Collar", "Plumbing Jacks", "Splitboot"],
    },
    {
        label: "Drip Edge, Flashing & Valley",
        items: [
            "Drip Edge / Eave",
            "Drip Edge / Rake",
            "Step Flashing",
            "Counter Flashing",
            "Valley metal",
            "Roll Valley",
            "Head Wall Metal",
        ],
    },
    {
        label: "Equipment & Gutters",
        items: ["Gutters", "Downspouts", "Gutter Screens"],
    },
    {
        label: "Fasteners & Consumables",
        items: ["Roof Nails", "H&R Nails", "Nails - Plastic cap nails", "Sealant / Adhesive", "Spray Paint"],
    },
];

const ALL_GROUPED_ITEMS = MATERIAL_GROUPS.flatMap((g) => g.items);

const EXCLUDED_CATEGORIES: string[] = [...HIDDEN_MATERIAL_CATEGORIES];

// ─── Main Component ──────────────────────────────────────────────────────────

function MaterialsContent({ measurements, attributes, setIsNextStageEligible }: MaterialsProps) {
    const {
        materialSchema,
        priceRanges,
        isLoading,
        missingProducts,
        setOpenAdditionalProductDialog,
        selectedValues,
        handleQuantityUpdate,
        handleProductChange,
        handleColorChange,
        handleClearProduct,
        handleReevaluateFormula,
        handleLineProductChange,
        handleLineQuantityUpdate,
        handleAddLine,
        handleRemoveLine,
        handleClearMultiLine,
    } = useMaterials({ measurements, attributes });

    // Build grouped items from the loaded schema
    const getGroupItems = (groupItemNames: string[]) =>
        groupItemNames.map((name) => materialSchema.find((item: any) => item.Name === name)).filter(Boolean);

    const ungroupedItems = materialSchema.filter(
        (item: any) =>
            isVisibleMaterialCategory(item.Name) &&
            !ALL_GROUPED_ITEMS.includes(item.Name) &&
            !EXCLUDED_CATEGORIES.includes(item.Name),
    );

    const sectionHeaderSx = {
        color: "text.tertiary",
        mb: 1.5,
        mt: 3,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        fontSize: "11px",
        fontWeight: "xl",
    };

    const renderMaterialRow = (item: any) => {
        if (isMultiLineCategory(item.Name)) {
            return (
                <Grid key={item.Name} xs={12}>
                    <MultiLineMaterialRow
                        item={item}
                        selection={selectedValues[item.Name] || {}}
                        selectedValues={selectedValues}
                        priceRanges={priceRanges}
                        onLineProductChange={(lineKey, product) => handleLineProductChange(item.Name, lineKey, product)}
                        onLineQuantityUpdate={(lineKey, dir) => handleLineQuantityUpdate(item.Name, lineKey, dir)}
                        onAddLine={() => handleAddLine(item.Name)}
                        onRemoveLine={(lineKey) => handleRemoveLine(item.Name, lineKey)}
                        onClearAll={() => handleClearMultiLine(item.Name)}
                        onReevaluate={() => handleReevaluateFormula(item.Name)}
                    />
                </Grid>
            );
        }

        return (
            <Grid key={item.Name} xs={12}>
                <MaterialRow
                    item={item}
                    selection={selectedValues[item.Name] || {}}
                    selectedValues={selectedValues}
                    priceRanges={priceRanges}
                    onProductChange={(val) => handleProductChange(val, item.Name)}
                    onColorChange={(color, mId) => handleColorChange(item.Name, color, mId)}
                    onQuantityUpdate={(dir) => handleQuantityUpdate(item.Name, dir)}
                    onRemove={() => handleClearProduct(item.Name)}
                    onReevaluate={() => handleReevaluateFormula(item.Name)}
                />
            </Grid>
        );
    };

    return (
        <AnimatePresence mode="popLayout">
            {isLoading ? (
                <motion.div
                    key="skeleton"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <MaterialsSkeleton />
                </motion.div>
            ) : (
                <motion.div
                    key="content"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <Box>
                        <Box mb={2}>
                            <Typography level="title-md" startDecorator={<Package size={16} />}>
                                Material & Component Selection
                            </Typography>
                            <Typography level="body-sm" color="neutral">
                                Product specifications, manufacturer comparisons, and inventory counts.
                            </Typography>
                        </Box>

                        {/* Grouped Product Cards */}
                        {MATERIAL_GROUPS.map((group) => {
                            const items = getGroupItems(group.items);
                            if (items.length === 0) return null;
                            return (
                                <Box key={group.label}>
                                    <Typography level="title-sm" sx={sectionHeaderSx}>
                                        {group.label}
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {items.map(renderMaterialRow)}
                                    </Grid>
                                </Box>
                            );
                        })}

                        {/* Ungrouped / Other */}
                        {ungroupedItems.length > 0 && (
                            <Box>
                                <Typography level="title-sm" sx={sectionHeaderSx}>
                                    Other
                                </Typography>
                                <Grid container spacing={2}>
                                    {ungroupedItems.map(renderMaterialRow)}
                                </Grid>
                            </Box>
                        )}

                        {/* Missing Products */}
                        {missingProducts.length > 0 && (
                            <Box mt={4} mb={2} px={1.5}>
                                <Typography level="body-xs" color="warning" mb={1} sx={{ fontWeight: 600 }}>
                                    MISSING PRODUCTS ({missingProducts.length})
                                </Typography>
                                <Grid container spacing={2}>
                                    {missingProducts.map((pName: string) => (
                                        <Grid key={pName} xs={12} md={6}>
                                            <Box
                                                sx={{
                                                    p: 1.5,
                                                    border: "1px dashed",
                                                    borderColor: "warning.300",
                                                    borderRadius: "md",
                                                    bgcolor: "warning.50",
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                                                    {pName}
                                                </Typography>
                                                <Typography
                                                    level="body-xs"
                                                    component="a"
                                                    href="#"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setOpenAdditionalProductDialog(true);
                                                    }}
                                                    sx={{ cursor: "pointer", textDecoration: "underline" }}
                                                >
                                                    Add Missing
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        )}

                        {/* Supplier Comparison & Selection */}
                        <Box mt={4}>
                            <SupplierComparison type="Manufacturer" selectedValues={selectedValues} />
                        </Box>
                    </Box>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

// ─── Export with context provider ────────────────────────────────────────────
// SelectedSupplierProvider is required by SupplierComparison to share the
// selected supplier state between the grid and the price display components.

export default function Materials({ measurements, attributes, setIsNextStageEligible }: MaterialsProps) {
    return <MaterialsContent measurements={measurements} attributes={attributes} setIsNextStageEligible={setIsNextStageEligible} />;
}
